import React, { useEffect, useState } from 'react';
import { X, AlertCircle } from 'lucide-react';

// Motivos disponibles al cerrar un activo. Se mantiene sincronizado con
// `DESCARTE_MOTIVOS` en AssetsPage.js (mismo pipeline backend `descarte_motivos`).
export const CLOSURE_REASONS = [
  { v: 'FUERA_ZONA', l: 'Fuera de zona operativa' },
  { v: 'POCA_LIQUIDEZ', l: 'Ubicación con poca liquidez' },
  { v: 'DESCARTADO_PREVIAMENTE', l: 'Descartado previamente' },
  { v: 'NO_INMUEBLE_LOCAL', l: 'No inmueble / local' },
  { v: 'NO_RENTABLE', l: 'No rentable' },
  { v: 'ACTIVO_DESACTUALIZADO', l: 'Activo desactualizado (revisar estado judicial)' },
  { v: 'ACTIVO_QUEMADO', l: 'Activo quemado / ya trabajado' },
  { v: 'INFO_INSUFICIENTE', l: 'Información insuficiente' },
  { v: 'OCUPACION_CRITICA', l: 'Problema de ocupación crítico' },
  { v: 'ADQUIRIDA_POR_TERCERO', l: 'Adquirida por tercero' },
];

/**
 * ClosureReasonModal (Feb 2026)
 *
 * Popup OBLIGATORIO al cambiar el estado de un activo a CERRADO.
 * Debe usarse en TODOS los puntos de entrada de la app (ficha del
 * activo, base de activos individual, bulk selection, edit page).
 *
 * Props:
 *  - open: boolean — controla la visibilidad.
 *  - onClose(): cancela el cierre.
 *  - onConfirm(motivos: string[]): confirma con la lista de motivos.
 *  - assetCount: opcional — nº de activos (para bulk). Default 1.
 */
const ClosureReasonModal = ({ open, onClose, onConfirm, assetCount = 1 }) => {
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    if (open) setSelected([]);
  }, [open]);

  if (!open) return null;

  const canSubmit = selected.length > 0;
  const toggle = (v) => setSelected([v]); // radio behaviour

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(15, 23, 42, 0.55)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
      data-testid="closure-reason-overlay"
    >
      <div
        className="rounded-xl shadow-2xl max-w-md w-full"
        style={{ background: 'var(--color-bg-elevated)' }}
        onClick={(e) => e.stopPropagation()}
        data-testid="closure-reason-modal"
      >
        <div className="flex items-start justify-between p-5 border-b" style={{ borderColor: 'var(--color-border)' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-red-500 text-white">
              <AlertCircle className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-semibold text-base" style={{ color: 'var(--color-text-primary)' }}>
                Motivo de cierre {assetCount > 1 && `(${assetCount} activos)`}
              </h3>
              <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}>
                Selecciona el motivo para cerrar {assetCount > 1 ? 'los activos' : 'el activo'}.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-slate-700/20 rounded"
            aria-label="Cancelar"
            data-testid="closure-reason-close"
          >
            <X className="w-5 h-5" style={{ color: 'var(--color-text-muted)' }} />
          </button>
        </div>

        <div className="overflow-y-auto p-4 space-y-1 max-h-[55vh]">
          {CLOSURE_REASONS.map(m => (
            <label
              key={m.v}
              className="flex items-center gap-2 text-sm cursor-pointer hover:opacity-90 px-2 py-1.5 rounded"
              data-testid={`closure-reason-${m.v}`}
            >
              <input
                type="radio"
                name="closure-reason"
                checked={selected.includes(m.v)}
                onChange={() => toggle(m.v)}
                className="w-4 h-4 accent-red-500"
              />
              <span style={{ color: 'var(--color-text-primary)' }}>{m.l}</span>
            </label>
          ))}
        </div>

        <div className="p-4 border-t flex items-center justify-end gap-2" style={{ borderColor: 'var(--color-border)' }}>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium rounded border"
            style={{ color: 'var(--color-text-primary)', borderColor: 'var(--color-border)', background: 'transparent' }}
            data-testid="closure-reason-cancel"
          >
            Cancelar
          </button>
          <button
            onClick={() => canSubmit && onConfirm(selected)}
            disabled={!canSubmit}
            className={`px-4 py-2 text-sm font-semibold rounded ${
              canSubmit ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-slate-300 text-slate-500 cursor-not-allowed'
            }`}
            data-testid="closure-reason-submit"
          >
            Cerrar activo{assetCount > 1 ? 's' : ''}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClosureReasonModal;
